import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { getArticleBySlug, getRelatedArticles } from '@/mocks/articles';
import { getCategoryBySlug } from '@/mocks/categories';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const article = getArticleBySlug(slug || '');
  const category = article ? getCategoryBySlug(article.category) : null;
  const relatedArticles = article ? getRelatedArticles(article.slug, article.category, 3) : [];

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-20 text-center">
          <p className="text-foreground-500">文章不存在</p>
          <Link to="/" className="text-primary-600 hover:underline mt-4 inline-block">返回首页</Link>
        </main>
        <Footer />
      </div>
    );
  }

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${article.title} - 磁力网站大全推荐`,
    description: article.description,
    datePublished: article.addedDate,
    author: {
      '@type': 'Organization',
      name: '磁力网站大全',
    },
    publisher: {
      '@type': 'Organization',
      name: '磁力网站大全',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.ciliwangzhan.com/article/${article.slug}`,
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: category?.name || article.category, href: `/category/${article.category}` },
              { label: article.title },
            ]}
          />

          <article className="w-full py-10 md:py-14 bg-background-50">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-4xl mx-auto">
                <div className="mb-8">
                  <div className="flex flex-wrap items-center gap-3 mb-4">
                    {article.tags.map(tag => (
                      <span key={tag} className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-xs">
                        {tag}
                      </span>
                    ))}
                    <span className="text-xs text-foreground-400">{article.addedDate}</span>
                  </div>

                  <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-4">
                    {article.title} - 磁力网站推荐
                  </h1>
                  <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-6">
                    {article.description}
                  </p>

                  <div className="flex flex-wrap items-center gap-4 p-4 bg-background-100 rounded-xl">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-foreground-500">评分</span>
                      <span className="flex items-center gap-1 text-lg font-bold text-foreground-900">
                        <i className="ri-star-fill text-amber-400"></i>
                        {article.rating}
                      </span>
                      <span className="text-xs text-foreground-400">/ 5.0</span>
                    </div>
                    <div className="w-px h-6 bg-background-300"></div>
                    <div>
                      <span className="text-xs text-foreground-500 block">速度</span>
                      <span className="text-sm font-medium text-foreground-900">{article.speed}</span>
                    </div>
                    <div className="w-px h-6 bg-background-300"></div>
                    <div>
                      <span className="text-xs text-foreground-500 block">稳定性</span>
                      <span className="text-sm font-medium text-foreground-900">{article.stability}</span>
                    </div>
                    <div className="w-px h-6 bg-background-300"></div>
                    <div>
                      <span className="text-xs text-foreground-500 block">资源量</span>
                      <span className="text-sm font-medium text-foreground-900">{article.resources}</span>
                    </div>
                    <div className="ml-auto">
                      <a
                        href={article.url}
                        target="_blank"
                        rel="nofollow noopener"
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
                      >
                        <i className="ri-external-link-line"></i> 访问网站
                      </a>
                    </div>
                  </div>
                </div>

                <div className="relative rounded-xl overflow-hidden mb-10 h-64 md:h-80">
                  <img
                    src={article.imageUrl}
                    alt={article.title}
                    className="w-full h-full object-cover"
                  />
                </div>

                <div className="prose-custom mb-10">
                  <h2 className="text-xl font-heading font-bold text-foreground-900 mb-4">网站详细介绍</h2>
                  <p className="text-foreground-700 leading-relaxed text-sm md:text-base">{article.content}</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
                  <div className="p-5 bg-accent-50 rounded-xl border border-accent-100">
                    <h3 className="font-heading font-bold text-accent-800 mb-3 flex items-center gap-2">
                      <i className="ri-thumb-up-fill text-accent-500"></i> 优点
                    </h3>
                    <ul className="space-y-2">
                      {article.pros.map((pro, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-foreground-700">
                          <span className="w-5 h-5 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <i className="ri-check-line text-accent-600 text-xs"></i>
                          </span>
                          {pro}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="p-5 bg-secondary-50 rounded-xl border border-secondary-100">
                    <h3 className="font-heading font-bold text-secondary-800 mb-3 flex items-center gap-2">
                      <i className="ri-information-fill text-secondary-500"></i> 注意事项
                    </h3>
                    <ul className="space-y-2">
                      {article.cons.map((con, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-foreground-700">
                          <span className="w-5 h-5 rounded-full bg-secondary-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <i className="ri-close-line text-secondary-600 text-xs"></i>
                          </span>
                          {con}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {relatedArticles.length > 0 && (
                  <div className="border-t border-background-200/70 pt-10">
                    <h3 className="text-xl font-heading font-bold text-foreground-900 mb-6">
                      同类磁力网站推荐
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                      {relatedArticles.map(related => (
                        <Link
                          key={related.id}
                          to={`/article/${related.slug}`}
                          className="group bg-background-100 rounded-xl border border-background-200/70 hover:border-primary-200 transition-all overflow-hidden cursor-pointer"
                        >
                          <div className="h-36 overflow-hidden">
                            <img
                              src={related.thumbUrl}
                              alt={related.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            />
                          </div>
                          <div className="p-3">
                            <h4 className="font-heading font-bold text-foreground-900 text-sm group-hover:text-primary-600 transition-colors mb-1">
                              {related.title}
                            </h4>
                            <div className="flex items-center gap-1 text-xs text-foreground-500">
                              <i className="ri-star-fill text-amber-400 text-xs"></i>
                              {related.rating}
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </article>
        </main>
        <Footer />
      </div>
    </>
  );
}